import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { enablePersistence, initializeStorageCache } from './shared/lib/utils/safeStorage';
import { deferredPersistedStores } from './app/persistedStores';
import { hydrateDurableState } from './shared/lib/durableState/durableState';

// Check if we're in WebView2 environment
const isWebView2 = typeof window !== 'undefined' &&
  window.chrome?.webview !== undefined;
const shouldLogBootstrap = typeof window !== 'undefined' &&
  window.__PIPEEX_BOOT_TRACE === true;
const logBootstrap = (...args) => {
  if (shouldLogBootstrap) {
    console.log(...args);
  }
};

async function rehydratePersistedStores() {
  const results = await Promise.allSettled(
    deferredPersistedStores
      .filter((store) => store?.persist?.rehydrate)
      .map((store) => store.persist.rehydrate())
  );

  const rejectedCount = results.filter((result) => result.status === 'rejected').length;
  if (rejectedCount > 0) {
    console.warn('[index.js] Some persisted stores failed to rehydrate', { rejectedCount });
  }
}

function renderStartupError(title, message, detail = '') {
  return `
    <div style="min-height: 100vh; padding: 20px; box-sizing: border-box; font-family: sans-serif; color: var(--pipeex-text-primary); background: var(--pipeex-background-default);">
      <div style="max-width: 760px; margin: 0 auto; padding: 20px; border: 1px solid color-mix(in srgb, var(--pipeex-error-main) 26%, transparent); border-radius: 8px; background: var(--pipeex-background-paper);">
        <h2 style="margin: 0 0 12px; color: var(--pipeex-error-main);">${title}</h2>
        <p>${message}</p>
        ${detail}
        <button onclick="location.reload()" style="margin-top: 10px; padding: 8px 16px; cursor: pointer;">
          Reload
        </button>
      </div>
    </div>
  `;
}

let reactOwnsRoot = false;

window.onerror = function(message, source, lineno, colno, error) {
  console.error('[GLOBAL ERROR]', { message, source, lineno, colno, error });
  // Show error on screen if root exists
  const root = document.getElementById('root');
  if (root && !reactOwnsRoot) {
    root.innerHTML = renderStartupError(
      'Application Error',
      message,
      `<p style="font-size: 12px; color: var(--pipeex-text-secondary);">Source: ${source}:${lineno}:${colno}</p>`
    );
  }
  return true;
};

// Catch unhandled promise rejections
window.onunhandledrejection = function(event) {
  console.error('[UNHANDLED REJECTION]', event.reason);
};

logBootstrap('[index.js] Starting application...', { isWebView2 });

async function bootstrap() {
  // Restore plugin-mirrored workflow markers into localStorage BEFORE the first
  // render, so resume/navigation reads see them even after a WebView2 user-data
  // purge. Bounded internally and must never delay startup on failure.
  try {
    await hydrateDurableState();
  } catch (err) {
    console.warn('[index.js] Durable state hydration failed', err);
  }

try {
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    throw new Error('Root element not found in DOM');
  }

  logBootstrap('[index.js] Root element found, creating React root...');
  const root = ReactDOM.createRoot(rootElement);
  reactOwnsRoot = true;

  logBootstrap('[index.js] Rendering App...');

  // In WebView2, don't use StrictMode as it can cause issues
  if (isWebView2) {
    root.render(<App />);
  } else {
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  }

  logBootstrap('[index.js] App rendered successfully');

  // After initial render, enable persistence and initialize storage cache
  // This is done after render to prevent blocking the UI thread
  // Use a longer delay to ensure the app is fully interactive first
  setTimeout(() => {
    logBootstrap('[index.js] Enabling persistence after initial render...');
    initializeStorageCache().then(() => {
      enablePersistence();
      logBootstrap('[index.js] Persistence enabled');
      return rehydratePersistedStores();
    }).catch((err) => {
      console.warn('[index.js] Failed to initialize storage:', err);
    });
  }, 500);

} catch (error) {
  console.error('[index.js] Failed to render app:', error);
  const root = document.getElementById('root');
  if (root && !reactOwnsRoot) {
    root.innerHTML = renderStartupError(
      'Failed to Start Application',
      error.message,
      `<pre style="font-size: 11px; color: var(--pipeex-text-secondary); overflow: auto;">${error.stack}</pre>`
    );
  }
}

}

void bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
